/**
 * ACG 幸運地圖計算引擎（前端純 JS 版）
 * 原理：Astro*Carto*Graphy（Jim Lewis, 1978）
 * 固定出生 UTC 瞬間，找出 10 顆行星正好落在四個尖軸（MC/IC/ASC/DSC）的全球地點連線
 * 移植自 acg_poc.py（精度：太陽 ~0.01°、月亮 ~0.3°、行星 ~0.1–0.6°）
 */

const D2R = Math.PI / 180.0;
const R2D = 180.0 / Math.PI;

// ─── 行星顏色 ───────────────────────────────────────────────────────────────
export const PLANET_COLORS = {
  Sun: "#F5A623",
  Moon: "#BBBBBB",
  Mercury: "#7ED321",
  Venus: "#E94FA1",
  Mars: "#D0021B",
  Jupiter: "#9013FE",
  Saturn: "#8B572A",
  Uranus: "#4A90D9",
  Neptune: "#50E3C2",
  Pluto: "#9B9B9B",
};

export const PLANET_ZH = {
  Sun: "太陽",
  Moon: "月亮",
  Mercury: "水星",
  Venus: "金星",
  Mars: "火星",
  Jupiter: "木星",
  Saturn: "土星",
  Uranus: "天王",
  Neptune: "海王",
  Pluto: "冥王",
};

export const PLANETS = [
  "Sun", "Moon", "Mercury", "Venus", "Mars",
  "Jupiter", "Saturn", "Uranus", "Neptune", "Pluto",
];

// ─── 克卜勒根數（JPL 1800–2050 近似） ────────────────────────────────────────
// 每列：a0, e0, I0, L0, w0, O0, da, de, dI, dL, dw, dO
const ELEM = {
  Mercury: [0.38709927, 0.20563593, 7.00497902, 252.25032350, 77.45779628, 48.33076593,
    0.00000037, 0.00001906, -0.00594749, 149472.67411175, 0.16047689, -0.12534081],
  Venus: [0.72333566, 0.00677672, 3.39467605, 181.97909950, 131.60246718, 76.67984255,
    0.00000390, -0.00004107, -0.00078890, 58517.81538729, 0.00268329, -0.27769418],
  Earth: [1.00000261, 0.01671123, -0.00001531, 100.46457166, 102.93768193, 0.0,
    0.00000562, -0.00004392, -0.01294668, 35999.37244981, 0.32327364, 0.0],
  Mars: [1.52371034, 0.09339410, 1.84969142, -4.55343205, -23.94362959, 49.55953891,
    0.00001847, 0.00007882, -0.00813131, 19140.30268499, 0.44441088, -0.29257343],
  Jupiter: [5.20288700, 0.04838624, 1.30439695, 34.39644051, 14.72847983, 100.47390909,
    -0.00011607, -0.00013253, -0.00183714, 3034.74612775, 0.21252668, 0.20469106],
  Saturn: [9.53667594, 0.05386179, 2.48599187, 49.95424423, 92.59887831, 113.66242448,
    -0.00125060, -0.00050991, 0.00193609, 1222.49362201, -0.41897216, -0.28867794],
  Uranus: [19.18916464, 0.04725744, 0.77263783, 313.23810451, 170.95427630, 74.01692503,
    -0.00196176, -0.00004397, -0.00242939, 428.48202785, 0.40805281, 0.04240589],
  Neptune: [30.06992276, 0.00859048, 1.77004347, -55.12002969, 44.96476227, 131.78422574,
    0.00026291, 0.00005105, 0.00035372, 218.45945325, -0.32241464, -0.00508664],
  Pluto: [39.48211675, 0.24882730, 17.14001206, 238.92903833, 224.06891629, 110.30393684,
    -0.00031596, 0.00005170, 0.00004818, 145.20780515, -0.04062942, -0.01183482],
};

// 外行星長週期修正項 [b, c, s, f]
const EXTRA = {
  Jupiter: [-0.00012452, 0.06064060, -0.35635438, 38.35125000],
  Saturn: [0.00025899, -0.13434469, 0.87320147, 38.35125000],
  Uranus: [0.00058331, -0.97731848, 0.17689245, 7.67025000],
  Neptune: [-0.00041348, 0.68346318, -0.10162547, 7.67025000],
  Pluto: [-0.01262724, 0.0, 0.0, 0.0],
};

// ─── 1. 時間：西曆 UTC → 儒略日 ─────────────────────────────────────────────
export function julianDay(y, m, d, hourUtc) {
  if (m <= 2) { y -= 1; m += 12; }
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  return (Math.floor(365.25 * (y + 4716))
    + Math.floor(30.6001 * (m + 1))
    + d + b - 1524.5 + hourUtc / 24.0);
}

// ─── 2. 黃赤交角、恆星時 ──────────────────────────────────────────────────────
export function obliquity(T) {
  return 23.439291 - 0.0130042 * T - 1.64e-7 * T * T + 5.04e-7 * T * T * T;
}

export function gmstDeg(jd, T) {
  const g = (280.46061837 + 360.98564736629 * (jd - 2451545.0)
    + 0.000387933 * T * T - T * T * T / 38710000.0);
  return ((g % 360.0) + 360.0) % 360.0;
}

// ─── 3. 行星日心 XYZ 座標（克卜勒方程） ─────────────────────────────────────
function helioXYZ(name, T) {
  const [a0, e0, I0, L0, w0, O0, da, de, dI, dL, dw, dO] = ELEM[name];
  const a = a0 + da * T;
  const e = e0 + de * T;
  const I = (I0 + dI * T) * D2R;
  const L = L0 + dL * T;
  const w = w0 + dw * T;  // ϖ
  const O = (O0 + dO * T) * D2R;  // Ω

  let M = L - w;
  if (EXTRA[name]) {
    const [b, c, s, f] = EXTRA[name];
    M += b * T * T + c * Math.cos(f * T * D2R) + s * Math.sin(f * T * D2R);
  }
  M = (((M % 360.0) + 360.0) % 360.0) * D2R;

  // 解克卜勒方程（Newton 迭代）
  let E = M + e * Math.sin(M);
  for (let i = 0; i < 12; i++) {
    E -= (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
  }

  const xp = a * (Math.cos(E) - e);
  const yp = a * Math.sqrt(1 - e * e) * Math.sin(E);

  const arg = (w - O0 - dO * T) * D2R; // ω = ϖ - Ω
  const cw = Math.cos(arg), sw = Math.sin(arg);
  const cO = Math.cos(O), sO = Math.sin(O);
  const cI = Math.cos(I), sI = Math.sin(I);

  return [
    (cw * cO - sw * sO * cI) * xp + (-sw * cO - cw * sO * cI) * yp,
    (cw * sO + sw * cO * cI) * xp + (-sw * sO + cw * cO * cI) * yp,
    (sw * sI) * xp + (cw * sI) * yp,
  ];
}

// ─── 4. 月亮黃經（Meeus 簡化級數，~0.3°） ───────────────────────────────────
function moonLon(T) {
  const Lp = 218.3164477 + 481267.88123421 * T;
  let D = (297.8501921 + 445267.1114034 * T) * D2R;
  let Msun = (357.5291092 + 35999.0502909 * T) * D2R;
  let Mp = (134.9633964 + 477198.8675055 * T) * D2R;
  let F = (93.2720950 + 483202.0175233 * T) * D2R;
  const lon = Lp
    + 6.288774 * Math.sin(Mp)
    + 1.274027 * Math.sin(2 * D - Mp)
    + 0.658314 * Math.sin(2 * D)
    + 0.213618 * Math.sin(2 * Mp)
    - 0.185116 * Math.sin(Msun)
    - 0.114332 * Math.sin(2 * F)
    + 0.058793 * Math.sin(2 * D - 2 * Mp)
    + 0.057066 * Math.sin(2 * D - Msun - Mp)
    + 0.053322 * Math.sin(2 * D + Mp)
    + 0.045758 * Math.sin(2 * D - Msun);
  return ((lon % 360.0) + 360.0) % 360.0;
}

// ─── 5. 10 顆行星地心黃道座標 (lon°, lat°) ─────────────────────────────────
function geocentricEcliptic(jd) {
  const T = (jd - 2451545.0) / 36525.0;
  const prec = 1.39689 * T; // 簡化歲差

  const earth = helioXYZ("Earth", T);
  const result = {};

  // 太陽：地球日心向量反向
  result["Sun"] = [
    ((Math.atan2(-earth[1], -earth[0]) * R2D + prec) % 360.0 + 360.0) % 360.0,
    0.0,
  ];

  // 月亮
  const Fval = (93.2720950 + 483202.0175233 * T) * D2R;
  result["Moon"] = [moonLon(T), 5.128 * Math.sin(Fval)];

  for (const name of ["Mercury", "Venus", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune", "Pluto"]) {
    const p = helioXYZ(name, T);
    const gx = p[0] - earth[0];
    const gy = p[1] - earth[1];
    const gz = p[2] - earth[2];
    const lon = ((Math.atan2(gy, gx) * R2D + prec) % 360.0 + 360.0) % 360.0;
    const lat = Math.atan2(gz, Math.sqrt(gx * gx + gy * gy)) * R2D;
    result[name] = [lon, lat];
  }
  return result;
}

// ─── 6. 黃道 → 赤道（RA, Dec） ───────────────────────────────────────────────
function eclToEqu(lonDeg, latDeg, epsDeg) {
  const l = lonDeg * D2R, b = latDeg * D2R, e = epsDeg * D2R;
  const ra = ((Math.atan2(
    Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e),
    Math.cos(l)
  ) * R2D) % 360.0 + 360.0) % 360.0;
  const dec = Math.asin(
    Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l)
  ) * R2D;
  return [ra, dec];
}

// ─── 7. 工具函式 ──────────────────────────────────────────────────────────────
function wrap180(x) {
  return ((x + 180.0) % 360.0 + 360.0) % 360.0 - 180.0;
}

function splitAntimeridian(points) {
  const segs = [];
  let cur = [points[0]];
  for (let i = 1; i < points.length; i++) {
    if (Math.abs(points[i][0] - points[i - 1][0]) > 180.0) {
      if (cur.length >= 2) segs.push(cur);
      cur = [points[i]];
    } else {
      cur.push(points[i]);
    }
  }
  if (cur.length >= 2) segs.push(cur);
  return segs;
}

// ─── 8. 生成單條 GeoJSON Feature ────────────────────────────────────────────
function makeLine(planet, angle, coords) {
  return {
    type: "Feature",
    properties: {
      planet,
      planet_zh: PLANET_ZH[planet],
      angle,
      color: PLANET_COLORS[planet],
      label: `${PLANET_ZH[planet]}-${angle}`,
    },
    geometry: { type: "LineString", coordinates: coords },
  };
}

// ─── 9. 主計算：輸出完整 GeoJSON FeatureCollection ──────────────────────────
export function computeAcgLines(jd) {
  const T = (jd - 2451545.0) / 36525.0;
  const eps = obliquity(T);
  const gmst = gmstDeg(jd, T);
  const geo = geocentricEcliptic(jd);
  const features = [];

  const latGrid = [];
  for (let la = -75; la <= 75; la += 1) latGrid.push(la);

  for (const planet of PLANETS) {
    const [lonEcl, latEcl] = geo[planet];
    const [ra, dec] = eclToEqu(lonEcl, latEcl, eps);

    // MC 線：垂直子午線
    const lonMc = wrap180(ra - gmst);
    features.push(makeLine(planet, "MC", latGrid.map(la => [lonMc, la])));

    // IC 線：MC 對向
    const lonIc = wrap180(ra - gmst + 180.0);
    features.push(makeLine(planet, "IC", latGrid.map(la => [lonIc, la])));

    // ASC / DSC 線：逐緯度求地平線
    const ascPts = [], dscPts = [];
    for (const la of latGrid) {
      const cosH = -Math.tan(la * D2R) * Math.tan(dec * D2R);
      if (cosH < -1.0 || cosH > 1.0) continue; // 恆顯/恆隱
      const H0 = Math.acos(cosH) * R2D;
      ascPts.push([wrap180(ra - H0 - gmst), la]);
      dscPts.push([wrap180(ra + H0 - gmst), la]);
    }
    if (ascPts.length >= 2) {
      splitAntimeridian(ascPts).forEach(seg => features.push(makeLine(planet, "ASC", seg)));
    }
    if (dscPts.length >= 2) {
      splitAntimeridian(dscPts).forEach(seg => features.push(makeLine(planet, "DSC", seg)));
    }
  }

  return {
    type: "FeatureCollection",
    features,
  };
}

// ─── 10. 對外主入口 ──────────────────────────────────────────────────────────
/**
 * @param {string} dateStr   "YYYY-MM-DD"
 * @param {string} timeStr   "HH:MM"
 * @param {boolean} isDaylight  日光節約時間
 * @param {number} tzOffset  時區偏移（預設 +8）
 * @returns GeoJSON FeatureCollection
 */
export function computeAcgFromBirth(dateStr, timeStr, isDaylight = false, tzOffset = 8) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  let hourLocal = hh + mm / 60.0;
  if (isDaylight) hourLocal -= 1.0; // 日光節約：本地時間往前調 1 小時以還原標準時
  const hourUtc = hourLocal - tzOffset;
  const jd = julianDay(y, m, d, hourUtc);
  return computeAcgLines(jd);
}
